import React, { useMemo } from "react";
import i18n from "i18next";
import k from "src/i18n/keys";

import { Text } from "@unifize/sarah";

import useFetch from "src/hooks/useFetch";
import Icon from "src/icons";
import ApproverPhoto from "src/components/user/Photo";
import * as colors from "src/styles/constants/colors";

import {
  Approval as StyledApproval,
  ApprovalSection,
  ApprovalHeader,
  BadgeIdContainer,
  StatusBadge,
  ImagesContainer,
  SignatureImageDisplay,
  LoadingText,
  CommentText,
} from "./styles";

type ApprovalData = {
  approved?: boolean;
  id?: string;
  signature?: string | null;
  comment?: string | null;
  signer?: string | null;
  request?: boolean;
  cancelled?: boolean;
};

type Props = { data: ApprovalData };

const Approval = ({ data }: Props) => {
  const { approved, id, signature, comment, signer, request, cancelled } = data;

  type SignedUrlResponse = { url?: string } | null;
  const { response: signatureFetchResponse, isLoading: signatureLoading } =
    useFetch(signature ? `/signature/signed-url/${signature}` : null, {
      method: "GET",
    }) as { response: SignedUrlResponse; isLoading: boolean };

  const statusText = useMemo(() => {
    if (request === true) {
      return "REQUESTED";
    }
    if (cancelled === true) {
      return "CANCELLED";
    }
    return approved ? "APPROVED" : "REJECTED";
  }, [approved, request, cancelled]);

  const checkmarkIcon = useMemo(() => {
    if (request === true) {
      return "alarmClock";
    }
    return approved ? "tick" : "filledCross";
  }, [approved, request]);

  const backgroundColor = useMemo<{ light: string; dark: string }>(() => {
    if (request === true) {
      return {
        light: colors.lightBlue25,
        dark: colors.active,
      };
    }
    if (cancelled === true) {
      return {
        light: colors.skyLighter,
        dark: colors.grey42,
      };
    }
    return approved
      ? {
          light: "#F6FEF9",
          dark: "#039855",
        }
      : {
          light: "#FFFBFA",
          dark: "#D92D20",
        };
  }, [approved, request, cancelled]);

  return (
    <StyledApproval
      bg={request == undefined ? backgroundColor.light : "transparent"}
    >
      <ApprovalSection>
        <ApprovalHeader>
          <BadgeIdContainer>
            <StatusBadge bg={backgroundColor.dark}>
              <Icon
                type={checkmarkIcon}
                fill={colors.brightLight}
                color={backgroundColor.dark}
              />
              <Text variant="caption" color={colors.brightLight}>
                {statusText}
              </Text>
            </StatusBadge>
          </BadgeIdContainer>
          <ImagesContainer>
            {signer ? <ApproverPhoto uid={signer} size="xs" /> : null}
            {signature ? (
              signatureLoading ? (
                <LoadingText>{i18n.t(k.LOADING)}</LoadingText>
              ) : signatureFetchResponse?.url ? (
                <SignatureImageDisplay
                  src={signatureFetchResponse.url}
                  alt="Signature"
                />
              ) : null
            ) : null}
          </ImagesContainer>
        </ApprovalHeader>
        {id && (
          <Text variant="caption" color={colors.primary} breakWord>
            {`${i18n.t(k.ID)} ${id.substring(0, 6)}...${id.substring(
              id.length - 6,
            )}`}
          </Text>
        )}
      </ApprovalSection>
      {comment && <CommentText>{comment}</CommentText>}
    </StyledApproval>
  );
};

export default Approval;
